import React, { useEffect, useState } from "react";

interface LinkPreviewProps {
  url: string;
}

const STYLE_ID = "link-preview-styles";

const STYLES = `
.lp-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 220px;
  padding: 4px 10px 4px 7px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.04);
  color: rgba(232, 237, 244, 0.72);
  font-size: 0.85em;
  line-height: 1.5;
  text-decoration: none;
  transition: background 140ms ease, border-color 140ms ease, color 140ms ease;
}
.lp-pill:hover {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.18);
  color: #e8edf4;
  text-decoration: none;
}
.lp-favicon {
  flex: none;
  width: 14px;
  height: 14px;
  border-radius: 3px;
}
/* Placeholder keeps the pill from reflowing when a favicon fails to load. */
.lp-favicon-fallback {
  flex: none;
  width: 14px;
  height: 14px;
  border-radius: 3px;
  background: rgba(255, 255, 255, 0.14);
}
.lp-domain {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
`;

const useStyleSheet = () => {
  useEffect(() => {
    if (document.getElementById(STYLE_ID)) return;
    const el = document.createElement("style");
    el.id = STYLE_ID;
    el.textContent = STYLES;
    document.head.appendChild(el);
  }, []);
};

const extractDomain = (inputUrl: string): string => {
  try {
    return new URL(inputUrl).hostname.replace(/^www\./, "");
  } catch {
    return "Unknown";
  }
};

const LinkPreview: React.FC<LinkPreviewProps> = ({ url }) => {
  const [favicon, setFavicon] = useState<string>("");
  const [faviconFailed, setFaviconFailed] = useState(false);
  useStyleSheet();

  useEffect(() => {
    setFaviconFailed(false);
    try {
      const domain = new URL(url).hostname;
      setFavicon(`https://www.google.com/s2/favicons?domain=${domain}&sz=32`);
    } catch {
      setFaviconFailed(true);
    }
  }, [url]);

  const domain = extractDomain(url);

  return (
    <a
      className="lp-pill"
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      title={url}
    >
      {favicon && !faviconFailed ? (
        <img
          className="lp-favicon"
          src={favicon}
          alt=""
          onError={() => setFaviconFailed(true)}
        />
      ) : (
        <span className="lp-favicon-fallback" aria-hidden="true" />
      )}
      <span className="lp-domain">{domain}</span>
    </a>
  );
};

export default LinkPreview;
